import { matchPath } from "react-router-dom";

import {
  enumeratedLiterals,
  type EnumeratedLiteralType,
  type EnumeratedLiteralModel,
} from "@north-beam/nb-common";

import { type AbstractUser } from "../user";

export const Pages = enumeratedLiterals(
  [
    "home",
    "attribution",
    "orders",
    "order-details",
    "model-comparison",
    "sales",
    "customers",
    "overview",
    "settings-apex",
    "settings-connections",
    "settings-manage-exports",
    "settings-model-settings",
    "creative-analytics",
    "mmm",
    "breakdowns",
    "breakdown_edit",
    "label_assignment",
    "onboarding",
    "metrics-explorer",
    "product-analytics",
    "labs",
    "landing-page-analytics",
    "documents",
    "dashboard-v2-orders",
    "dashboard-v2-order-details",
    "dashboard-v2-sales",
    "dashboard-v2-overview",
    "dashboard-v2-creative-analytics",
    "dashboard-v2-product-analytics",
    "sales-next",
  ] as const,
  {
    getLabels: (accessors) =>
      ({
        [accessors.HOME]: "Home",
        [accessors.ATTRIBUTION]: "Attribution",
        [accessors.ORDERS]: "Orders",
        [accessors.ORDER_DETAILS]: "Order Details",
        [accessors.MODEL_COMPARISON]: "Model Comparison",
        [accessors.SALES]: (user: AbstractUser) =>
          !user.featureFlags.doNotShowNewSalesPage ? "Sales V3" : "Sales",
        [accessors.OVERVIEW]: (user: AbstractUser) =>
          user.featureFlags.overviewPageV2 ? "Overview V2" : "Overview",
        [accessors.SETTINGS_APEX]: "Settings | Apex",
        [accessors.SETTINGS_CONNECTIONS]: "Settings | Connections",
        [accessors.SETTINGS_MANAGE_EXPORTS]: "Settings | Manage Exports",
        [accessors.SETTINGS_MODEL_SETTINGS]: "Settings | Model Settings",
        [accessors.CREATIVE_ANALYTICS]: "Creative Analytics",
        [accessors.MMM]: "MMM",
        [accessors.BREAKDOWNS]: "Breakdowns",
        [accessors.BREAKDOWN_EDIT]: "Breakdown Edit",
        [accessors.CUSTOMERS]: "Customers",
        [accessors.LABEL_ASSIGNMENT]: "Label assignment",
        [accessors.ONBOARDING]: "Onboarding",
        [accessors.METRICS_EXPLORER]: "Metrics Explorer",
        [accessors.LABS]: "Labs",
        [accessors.LANDING_PAGE_ANALYTICS]: "Landing Page Analytics",
        [accessors.PRODUCT_ANALYTICS]: "Product Analytics",
        [accessors.DOCUMENTS]: "Documents",
        [accessors.DASHBOARD_V2_ORDERS]: "Orders",
        [accessors.DASHBOARD_V2_ORDER_DETAILS]: "Order Details",
        [accessors.DASHBOARD_V2_SALES]: "Sales",
        [accessors.DASHBOARD_V2_OVERVIEW]: "Overview",
        [accessors.DASHBOARD_V2_CREATIVE_ANALYTICS]: "Creative Analytics",
        [accessors.DASHBOARD_V2_PRODUCT_ANALYTICS]: "Product Analytics",
        [accessors.SALES_NEXT]: "Sales",
      } as const),
    getAttributes: (accessors) =>
      ({
        [accessors.HOME]: { pathMatch: "/:userId/home", version: "v1" },
        [accessors.ATTRIBUTION]: {
          pathMatch: "/:userId/attribution",
          version: "v1",
        },
        [accessors.ORDERS]: { pathMatch: "/:userId/orders", version: "v1" },
        [accessors.ORDER_DETAILS]: {
          pathMatch: "/:userId/orders/:orderId",
          version: "v1",
        },
        [accessors.SALES]: {
          pathMatch: "/:userId/sales",
          version: (user: AbstractUser) =>
            !user.featureFlags.doNotShowNewSalesPage ? "v3" : "v1",
        },
        [accessors.CUSTOMERS]: {
          pathMatch: "/:userId/customers",
          version: (user: AbstractUser) =>
            user.featureFlags.customerLtvV2 ? "v2" : "v1",
        },
        [accessors.OVERVIEW]: {
          pathMatch: "/:userId/overview",
          version: (user: AbstractUser) =>
            user.featureFlags.overviewPageV2 ? "v2" : "v1",
        },
        [accessors.SETTINGS_APEX]: {
          pathMatch: "/:userId/settings/apex",
          version: "v1",
        },
        [accessors.SETTINGS_CONNECTIONS]: {
          pathMatch: "/:userId/settings/connections",
          version: "v1",
        },
        [accessors.SETTINGS_MANAGE_EXPORTS]: {
          pathMatch: "/:userId/settings/manage-exports",
          version: "v1",
        },
        [accessors.SETTINGS_MODEL_SETTINGS]: {
          pathMatch: "/:userId/settings/model-settings",
          version: "v1",
        },
        [accessors.MMM]: { pathMatch: "/:userId/mmm", version: "v1" },
        [accessors.CREATIVE_ANALYTICS]: {
          pathMatch: "/:userId/creative-analytics",
          version: "v1",
        },
        [accessors.BREAKDOWNS]: {
          pathMatch: "/:userId/breakdowns",
          version: "v1",
        },
        [accessors.BREAKDOWN_EDIT]: {
          pathMatch: "/:userId/breakdowns/:id/edit",
          version: "v1",
        },
        [accessors.LABEL_ASSIGNMENT]: {
          pathMatch: "/:userId/label-assignment",
          version: "v1",
        },
        [accessors.ONBOARDING]: {
          pathMatch: "/:userId/onboarding",
          version: "v1",
        },
        [accessors.MODEL_COMPARISON]: { pathMatch: null, version: "v1" },
        [accessors.METRICS_EXPLORER]: {
          pathMatch: "/:userId/metrics-explorer",
          version: "v1",
        },
        [accessors.PRODUCT_ANALYTICS]: {
          pathMatch: "/:userId/product-analytics",
          version: "v1",
        },
        [accessors.LABS]: {
          pathMatch: "/:userId/labs",
          version: "v1",
        },
        [accessors.LANDING_PAGE_ANALYTICS]: {
          pathMatch: "/:userId/labs/landing-page-analytics",
          version: "v1",
        },
        [accessors.DOCUMENTS]: {
          pathMatch: "/:userId/customers/documents",
          version: "v1",
        },
        [accessors.DASHBOARD_V2_ORDERS]: {
          pathMatch: "/:userId/attribution/orders",
          version: "v1",
        },
        [accessors.DASHBOARD_V2_ORDER_DETAILS]: {
          pathMatch: "/:userId/attribution/orders/:orderId",
          version: "v1",
        },
        [accessors.DASHBOARD_V2_SALES]: {
          pathMatch: "/:userId/attribution/sales",
          version: (user: AbstractUser) =>
            !user.featureFlags.doNotShowNewSalesPage ? "v3" : "v1",
        },
        [accessors.DASHBOARD_V2_OVERVIEW]: {
          pathMatch: "/:userId/home/overview",
          version: (user: AbstractUser) =>
            user.featureFlags.overviewPageV2 ? "v2" : "v1",
        },
        [accessors.DASHBOARD_V2_CREATIVE_ANALYTICS]: {
          pathMatch: "/:userId/attribution/creative-analytics",
          version: "v1",
        },
        [accessors.DASHBOARD_V2_PRODUCT_ANALYTICS]: {
          pathMatch: "/:userId/attribution/product-analytics",
          version: "v1",
        },
        [accessors.SALES_NEXT]: {
          pathMatch: "/:userId/sales-next",
          version: "v1",
        },
      } as const),
  },
);

export type PageId = EnumeratedLiteralType<typeof Pages>;
export type Page<I extends PageId | undefined = undefined> =
  EnumeratedLiteralModel<typeof Pages, I>;

export type PageLabel<I extends PageId> = Page<I>["label"] extends (
  user: AbstractUser,
) => infer L
  ? L
  : Page<I>["label"];

export type PageVersion<I extends PageId = PageId> = I extends PageId
  ? Page<I>["version"] extends (user: AbstractUser) => infer V
    ? V
    : Page<I>["version"]
  : never;

type FindPageForPathOptions = { readonly strict?: boolean };

type FindPageForPathRT<O extends FindPageForPathOptions> = O extends {
  readonly strict: true;
}
  ? Page
  : Page | null;

export const findPageForPath = <O extends FindPageForPathOptions>(
  pathname: string,
  options: O,
): FindPageForPathRT<O> => {
  const filtered = Pages.data.filter((d) => {
    if (d.pathMatch !== null) {
      return matchPath({ path: d.pathMatch }, pathname);
    }
    return false;
  });
  if (filtered.length > 1) {
    throw new Error(`Multiple pages matched for path '${pathname}'!`);
  } else if (filtered.length === 0) {
    if (options.strict !== false) {
      throw new Error(`No page was was found for path '${pathname}'!`);
    }
    return null as FindPageForPathRT<O>;
  }
  return filtered[0] as FindPageForPathRT<O>;
};
